import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { SignalRow } from './SignalRow';
import type { ReviewableSignal } from './types';

interface SummaryStepProps {
  signals: ReviewableSignal[];
  onToggleExclude: (index: number) => void;
  onEditSignal: (index: number) => void;
  onBack: () => void;
  onConfirm: () => void;
  isCreating: boolean;
}

export const SummaryStep = ({
  signals,
  onToggleExclude,
  onEditSignal,
  onBack,
  onConfirm,
  isCreating,
}: SummaryStepProps) => {
  const { t } = useTranslation('knowledge');

  const includedCount = useMemo(
    () => signals.filter((signal) => !signal.excluded).length,
    [signals]
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold">
          {t('signalReview.createSignals')}
        </h2>
        <p className="text-[13px] text-muted-foreground">
          {t('signalReview.summaryDescription')}
        </p>
      </div>

      {/* Signal list */}
      <div className="flex flex-col gap-2 max-h-[340px] overflow-y-auto pe-1">
        {signals.map((signal) => (
          <SignalRow
            key={signal.index}
            signal={signal}
            onToggleExclude={onToggleExclude}
            onEdit={onEditSignal}
          />
        ))}
      </div>

      {/* Count */}
      <div className="text-xs text-muted-foreground">
        {t('signalReview.selectedCount', {
          included: includedCount,
          total: signals.length,
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t pt-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-3.5 w-3.5 rtl:rotate-180" />
          {t('signalReview.backToDefaults')}
        </Button>
        <Button
          size="sm"
          onClick={onConfirm}
          disabled={includedCount === 0 || isCreating}
          className="gap-1.5"
        >
          {isCreating ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('creatingSignals')}
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5" />
              {includedCount === 1
                ? t('importModal.createSignals_one', { count: 1 })
                : t('importModal.createSignals_other', {
                    count: includedCount,
                  })}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
